import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import { AppName } from '@west-australian-newspapers/publication-types'
import { CardItem } from '../client-data-types/card-types'
import { PublicationType } from '../client-data-types/publication'
import { Topic } from '../client-data-types/topic'
import {
    isAnchorLink,
    isExternalLink,
    isNonHttpLink,
} from '../helpers/link-utils'
import { Product } from '../__product'

export interface PublicationRouteHint {
    routeKind: 'publication'
    publicationKind: PublicationType['kind']
    heading: string
    homepageHead: string | undefined
    homepageTeaser: string | undefined
    primaryTopic: Topic
    secondaryTopics: Topic[]
    imageCount?: number
}

export interface TopicRouteHint {
    routeKind: 'topic'
    topic: Topic
    publicationKind: PublicationType['kind']
}

export type RouteHints = PublicationRouteHint | TopicRouteHint
export interface LocationDescriptorWithRouteHint {
    pathname: string
    state: {
        routeHint: RouteHints
    }
}

export function toTopicRouteHint(
    topic: Topic,
): LocationDescriptorWithRouteHint {
    const routeHint: TopicRouteHint = {
        routeKind: 'topic',
        topic: topic,
        publicationKind: 'article',
    }

    return {
        pathname: `/${topic.id}`,
        state: {
            routeHint,
        },
    }
}

interface ToLinkWithHintOptions {
    product: Product
    externalCuration?: AppName
}
export function toLinkWithHint(
    props: MaybeLoaded<CardItem>,
    options?: ToLinkWithHintOptions,
): LocationDescriptorWithRouteHint | string {
    if (!props.loaded) {
        return '/'
    }

    if (props.result.cardType === 'marketing-redirect-tile') {
        return props.result.url
    }

    const { link, _self } = props.result

    // if it's from external curation
    if (options?.externalCuration) {
        return getExternalLinkWithUtmTags({
            url: _self,
            product: options.product,
            externalCuration: options.externalCuration,
            headline: props.result.shortHeadline,
        })
    }

    const isExternal =
        isExternalLink(link) || isAnchorLink(link) || isNonHttpLink(link)

    if (isExternal) {
        return link
    }

    const routeHint: PublicationRouteHint = {
        routeKind: 'publication',
        heading: props.result.headline,
        homepageHead: props.result.shortHeadline,
        homepageTeaser: props.result.teaser,
        primaryTopic: props.result.primaryTopic,
        secondaryTopics: props.result.secondaryTopics,
        publicationKind: props.result.publicationKind,
        imageCount: props.result.imageCount,
    }
    return {
        pathname: props.result.link,
        state: {
            routeHint,
        },
    }
}

function getExternalLinkWithUtmTags({
    url,
    product,
    externalCuration,
    headline,
}: {
    url: string
    product: Product
    externalCuration: AppName
    headline: string
}) {
    const utmParams = new URLSearchParams()
    utmParams.set('utm_source', product.replace(/\s/g, ''))
    utmParams.set('utm_medium', 'homepage-curation')
    utmParams.set('utm_campaign', `${externalCuration}-curation`)
    utmParams.set('utm_term', 'article')
    utmParams.set('utm_content', headline)

    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${utmParams.toString()}`
}
